<template>
    <!-- A group report's own page: New (templateId 'new') or Edit (an id).
         Report Details on the left (name, group, and the schema's send
         schedule fields); the report's settings cards on the right, the same
         cards as the On Demand page. -->
    <div class="flex w-full flex-col">
        <alert-toast    v-model:show="showError"
                        type="error"
                        title="Error"
                        :duration="0"
        >{{ errorText }}</alert-toast>

        <alert-toast    v-model:show="showUnsaved"
                        type="error"
                        title="Unsaved changes"
        >Save your changes first, then run the report.</alert-toast>

        <report-page-header class="mb-6"
                            :title="pageTitle"
                            :info="schema.info"
        >
            <template   #icon>
                <slot name="icon"></slot>
            </template>

            <template   #actions>
                <template v-if="isNew">
                    <a  :href="listUrl"
                        class="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium leading-5 text-gray-700 hover:bg-gray-50 hover:text-gray-700 hover:no-underline"
                    >Cancel</a>

                    <button v-if="canEdit"
                            type="button"
                            :disabled="saving"
                            class="inline-flex items-center gap-2 rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium leading-5 text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                            @click="save"
                    >
                        <tabler-icon    v-if="saving"
                                        name="loader"
                                        size-class="h-4 w-4 animate-spin"
                        ></tabler-icon>
                        Save Group Report
                    </button>
                </template>

                <template v-else-if="template">
                    <button v-if="canArchive"
                            type="button"
                            class="inline-flex items-center rounded-md border border-red-500 bg-white px-4 py-2 text-sm font-medium leading-5 text-red-600 hover:bg-red-50"
                            @click="showArchive = true"
                    >Archive</button>

                    <button v-if="canViewHistory"
                            type="button"
                            class="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium leading-5 text-gray-700 hover:bg-gray-50"
                            @click="openHistory"
                    >History</button>

                    <button v-if="canEdit"
                            type="button"
                            :disabled="saving || ! dirty"
                            class="inline-flex items-center gap-2 rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium leading-5 text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                            @click="save"
                    >
                        <tabler-icon    v-if="saving"
                                        name="loader"
                                        size-class="h-4 w-4 animate-spin"
                        ></tabler-icon>
                        Save Changes
                    </button>

                    <!-- Runs use the saved report, so with unsaved edits the
                         button says so in a toast instead of opening. -->
                    <action-menu    v-if="canRun"
                                    :items="runItems"
                    >
                        <template #trigger="{open, toggle}">
                            <button type="button"
                                    aria-haspopup="menu"
                                    :aria-expanded="open"
                                    class="inline-flex items-center gap-2 rounded-md border border-transparent bg-green-500 px-4 py-2 text-sm font-medium leading-5 text-white hover:bg-green-600"
                                    @click="dirty ? warnUnsaved() : toggle()"
                            >
                                <tabler-icon    name="chartBar"
                                                size-class="h-4 w-4"
                                ></tabler-icon>
                                Run Report
                                <tabler-icon    name="chevronDown"
                                                size-class="h-4 w-4"
                                ></tabler-icon>
                            </button>
                        </template>
                    </action-menu>
                </template>
            </template>
        </report-page-header>

        <div    v-if="! ready"
                class="flex items-center justify-center gap-2 py-16 text-sm text-gray-500"
        >
            <tabler-icon    name="loader"
                            size-class="h-4 w-4 animate-spin"
            ></tabler-icon>
            Loading
        </div>

        <div    v-else-if="! isNew && ! template"
                class="flex flex-col items-center gap-3 py-16 text-center"
        >
            <p class="m-0 text-base font-medium text-gray-900">This group report could not be found</p>
            <a  :href="listUrl"
                class="text-sm text-blue-600"
            >Back to Group Reports</a>
        </div>

        <div    v-else
                class="grid w-full grid-cols-1 items-start gap-6 lg:grid-cols-2"
        >
            <report-card title="Report Details">
                <div class="flex flex-col gap-4">
                    <div>
                        <label  for="group_report_name"
                                class="mb-1 block text-left text-sm font-semibold text-gray-900"
                        >Name</label>
                        <input  id="group_report_name"
                                v-model="form.name"
                                type="text"
                                placeholder="(enter a name)"
                                maxlength="255"
                                :disabled="! canEdit"
                                class="w-full rounded-md border px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                :class="errors.name ? 'border-red-500' : 'border-gray-300'"
                                :aria-invalid="!! errors.name"
                        >
                        <p  v-if="errors.name"
                            class="m-0 mt-1 text-xs text-red-500"
                        >{{ errors.name }}</p>
                    </div>

                    <div>
                        <label  for="group_report_group"
                                class="mb-1 block text-left text-sm font-semibold text-gray-900"
                        >Group</label>
                        <div class="flex items-center gap-3">
                            <select id="group_report_group"
                                    v-model="form.groupId"
                                    :disabled="! isNew || ! canEdit"
                                    class="w-full rounded-md border bg-white px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                                    :class="errors.groupId ? 'border-red-500' : 'border-gray-300'"
                                    :aria-invalid="!! errors.groupId"
                            >
                                <option :value="null">Select a Group</option>
                                <option v-for="group in groupOptions"
                                        :key="group.id"
                                        :value="group.id"
                                >{{ group.name }}</option>
                            </select>

                            <!-- A new tab, so a half-filled form is not lost. -->
                            <a  :href="form.groupId ? manageGroupHref : undefined"
                                target="_blank"
                                rel="noopener"
                                :aria-disabled="! form.groupId"
                                class="inline-flex shrink-0 items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium leading-5 text-gray-700 hover:bg-gray-50 hover:text-gray-700 hover:no-underline"
                                :class="{ 'pointer-events-none opacity-50': ! form.groupId }"
                            >Manage Group</a>
                        </div>
                        <p  v-if="errors.groupId"
                            class="m-0 mt-1 text-xs text-red-500"
                        >{{ errors.groupId }}</p>
                    </div>

                    <report-settings-panel  v-if="detailFields.length > 0"
                                            v-model="form.settings"
                                            v-model:interval="form.interval"
                                            :schema="detailsSchema"
                                            :is-savable="true"
                                            :timezone="timezone"
                                            :context="context"
                                            variant="bare"
                    ></report-settings-panel>
                </div>
            </report-card>

            <div class="flex flex-col gap-6">
                <report-settings-panel  v-model="form.settings"
                                        v-model:interval="form.interval"
                                        :schema="settingsSchema"
                                        :is-savable="false"
                                        :timezone="timezone"
                                        :context="context"
                                        variant="cards"
                ></report-settings-panel>
            </div>
        </div>

        <archive-group-report-modal v-model:show="showArchive"
                                    :name="template?.name ?? ''"
                                    :busy="archiving"
                                    @confirm="archive"
        ></archive-group-report-modal>
    </div>
</template>

<script>
import _ from "lodash"

import AlertToast from "@/components/elements/AlertToast.vue"
import ActionMenu from "@/components/elements/ActionMenu.vue"
import TablerIcon from "@/components/elements/TablerIcon.vue"
import ReportCard from "./ReportCard.vue"
import ReportPageHeader from "./ReportPageHeader.vue"
import ReportSettingsPanel from "./ReportSettingsPanel.vue"
import ArchiveGroupReportModal from "./ArchiveGroupReportModal.vue"
import {splitTemplateSchema} from "./templateSchema.js"
import {editableTemplateSettings, formFingerprint} from "./groupReportRows.js"

// The list page shows this once after a save or archive (see the shell).
export const GROUP_REPORT_TOAST_KEY = 'groupReportToast'

// A weekly report saved without a day is sent on Monday (BaseReport default).
const DEFAULT_WEEKDAY = 1

export default {
    name: "GroupReportForm",
    components: {AlertToast, ActionMenu, TablerIcon, ReportCard, ReportPageHeader, ReportSettingsPanel, ArchiveGroupReportModal},
    emits: ['run'],
    props: {
        schema: {
            type: Object,
            required: true,
        },
        // 'new', or the id of the group report being edited.
        templateId: {
            type: [String, Number],
            required: true,
        },
        // useReportTemplates() for this report.
        templatesApi: {
            type: Object,
            required: true,
        },
        // The report's own defaults, the starting point for a new group report.
        defaultSettings: {
            type: Object,
            default: () => ({}),
        },
        // The report's settings hook (e.g. Excursion's default reading times).
        prepareSettings: {
            type: Function,
            default: null,
        },
        listUrl: {
            type: String,
            required: true,
        },
        // (id) => URL of the report's sent-report history for one group report.
        historyUrl: {
            type: Function,
            required: true,
        },
        manageGroupUrl: {
            type: String,
            required: true,
        },
        timezone: {
            type: String,
            default: null,
        },
        context: {
            type: Object,
            default: () => ({}),
        },
        canEdit: {
            type: Boolean,
            default: false,
        },
        canArchive: {
            type: Boolean,
            default: false,
        },
        canViewHistory: {
            type: Boolean,
            default: false,
        },
        canRun: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            ready: false,
            template: null,
            form: {name: '', groupId: null, interval: 'monthly', settings: {}},
            original: '',
            errors: {},
            saving: false,
            archiving: false,
            showArchive: false,
            showError: false,
            showUnsaved: false,
            errorText: '',
        }
    },
    computed: {
        isNew() {
            return this.templateId === 'new'
        },
        pageTitle() {
            if (this.isNew) {
                return `New ${this.schema.title}`
            }
            return this.template?.name ?? this.schema.title
        },
        split() {
            return splitTemplateSchema(this.schema)
        },
        detailFields() {
            return this.split.detailFields
        },
        detailsSchema() {
            return {sections: [{title: 'Report Details', fields: this.detailFields}]}
        },
        settingsSchema() {
            return this.split.settingsSchema
        },
        // The groups page opens one group's editor from ?group=.
        manageGroupHref() {
            return `${this.manageGroupUrl}?group=${encodeURIComponent(this.form.groupId)}`
        },
        groupOptions() {
            return Object.entries(this.templatesApi.groups.value ?? {})
                .filter(([, group]) => group.can_view !== false)
                .map(([id, group]) => ({id: Number(id), name: group.GroupName}))
                .sort((a, b) => a.name.localeCompare(b.name))
        },
        dirty() {
            return this.ready && formFingerprint(this.form) !== this.original
        },
        runItems() {
            return [
                {label: 'Run for Me', icon: 'user', onSelect: () => this.$emit('run', {template: this.template, forGroup: false})},
                {label: 'Run for Group', icon: 'users', onSelect: () => this.$emit('run', {template: this.template, forGroup: true})},
            ]
        },
    },
    created() {
        this.load()
    },
    methods: {
        load() {
            const api = this.templatesApi
            const request = api.loaded.value ? Promise.resolve() : api.load()

            request
                .then(() => {
                    if (this.isNew) {
                        // Start from the site's default settings for this
                        // report; recipients belong to the default only.
                        const defaults = _.omit(api.defaultTemplate.value?.settings ?? {}, ['subscribedUsers'])
                        this.form = {
                            name: '',
                            groupId: null,
                            interval: api.defaultTemplate.value?.interval ?? 'monthly',
                            settings: {..._.cloneDeep(this.defaultSettings), ...defaults},
                        }
                    } else {
                        this.template = api.find(this.templateId) ?? null
                        if (this.template) {
                            this.form = {
                                name: this.template.name,
                                groupId: this.template.groupId,
                                interval: this.template.interval,
                                settings: editableTemplateSettings(this.template),
                            }
                        }
                    }
                    // The report's hook also shapes stored values for display
                    // (e.g. Room Condition's send times), as the old editor did.
                    if (this.prepareSettings) {
                        this.form.settings = this.prepareSettings(this.form.settings) ?? this.form.settings
                    }
                    this.original = formFingerprint(this.form)
                })
                .catch((error) => this.fail(error))
                .finally(() => {
                    this.ready = true
                })
        },
        validate() {
            const errors = {}
            if (this.form.name.trim() === '') {
                errors.name = 'Enter a name'
            }
            if (this.isNew && ! this.form.groupId) {
                errors.groupId = 'Choose a group'
            }
            this.errors = errors

            return Object.keys(errors).length === 0
        },
        settingsToSave() {
            let settings = _.cloneDeep(this.form.settings)
            if (this.prepareSettings) {
                settings = this.prepareSettings(settings) ?? settings
            }
            if (this.form.interval === 'weekly' && (settings.weekday === null || settings.weekday === undefined)) {
                settings.weekday = DEFAULT_WEEKDAY
            }
            // Recipients are the group's members, not a per-report list.
            delete settings.subscribedUsers

            return settings
        },
        save() {
            // A second click while the first save is in flight would create a
            // second group report.
            if (this.saving || ! this.validate()) {
                return
            }
            this.saving = true

            const payload = {
                templateName: this.form.name.trim(),
                interval: this.form.interval,
                settings: this.settingsToSave(),
            }
            const request = this.isNew
                ? this.templatesApi.create({...payload, groupId: this.form.groupId})
                : this.templatesApi.update(this.template.id, payload)

            request
                .then(() => this.leave('Group report saved'))
                .catch((error) => {
                    // Stay on the form with what the user typed.
                    this.saving = false
                    this.fail(error)
                })
        },
        archive() {
            if (this.archiving) {
                return
            }
            this.archiving = true

            this.templatesApi.archive(this.template.id)
                .then(() => this.leave('Group report archived'))
                .catch((error) => {
                    this.archiving = false
                    this.showArchive = false
                    this.fail(error)
                })
        },
        warnUnsaved() {
            this.showUnsaved = true
        },
        openHistory() {
            window.open(this.historyUrl(this.template.id), '_blank', 'noopener')
        },
        leave(message) {
            sessionStorage.setItem(GROUP_REPORT_TOAST_KEY, message)
            window.location.assign(this.listUrl)
        },
        fail(error) {
            this.errorText = this.templatesApi.errorMessages(error).join(' ')
            this.showError = true
        },
    },
}
</script>
